import { store } from '../../state/store';
import type { ChatMessage, ToolCallState, TurnState, WidgetState } from '../../types';
import { t } from '../../i18n';
import { ChatOrchestratorBase, type Constructor, CHAT_TIMEOUT_MS } from './orchestrator-base';

/** A store facade scoped to one turn - writes are dropped once the turn is no longer bound. */
export interface TurnScopedStore {
  setState: (partial: Partial<WidgetState>) => void;
  updateMessage: (id: string, partial: Partial<ChatMessage>) => void;
  removeMessage: (id: string) => void;
  addFileToMessage: (id: string, file: Parameters<typeof store.addFileToMessage>[1]) => void;
  addToolCall: (tc: ToolCallState) => void;
  updateToolCall: (id: string, partial: Partial<ToolCallState>) => void;
  clearToolCalls: () => void;
  requestInputFocus: () => void;
}

export function withTurnHandling<TBase extends Constructor<ChatOrchestratorBase>>(Base: TBase) {
  return class extends Base {
    /** Stops the turn currently bound to the visible UI (the Stop button's target). */
    interruptCurrentTurn(): boolean {
      const turnId = store.getState().boundTurnId;
      if (!turnId) return false;
      return this.interruptTurn(turnId);
    }

    /** Stops exactly the turn identified by turnId, wherever it's displayed (or not). */
    interruptTurn(turnId: string): boolean {
      const turn = this.turns.get(turnId);
      if (!turn) return false;

      turn.interrupted = true;
      turn.abortReason = 'user';
      turn.abortController.abort();

      const ts = this.turnScopedStore(turnId);
      this.cancelStreamingContentFlush(turn);
      store.setState({ isLoading: false });
      ts.clearToolCalls();

      const partialText = turn.accumulatedText.trim();
      if (partialText) {
        ts.updateMessage(turn.assistantId, {
          content: `${partialText}\n\n[[ stopped ]]`,
          isStreaming: false,
        });
      } else {
        ts.removeMessage(turn.assistantId);
      }

      this.drivingResponseTracker.reset();
      return true;
    }

    /** A store facade that only actually writes when turnId is still the visible/bound turn. */
    protected turnScopedStore(turnId: string): TurnScopedStore {
      const isBound = () => store.getState().boundTurnId === turnId;
      return {
        setState: (partial: Partial<WidgetState>) => { if (isBound()) store.setState(partial); },
        updateMessage: (id: string, partial: Partial<ChatMessage>) => { if (isBound()) store.updateMessage(id, partial); },
        removeMessage: (id: string) => { if (isBound()) store.removeMessage(id); },
        addFileToMessage: (id: string, file: Parameters<typeof store.addFileToMessage>[1]) => { if (isBound()) store.addFileToMessage(id, file); },
        addToolCall: (tc: ToolCallState) => { if (isBound()) store.addToolCall(tc); },
        updateToolCall: (id: string, partial: Partial<ToolCallState>) => { if (isBound()) store.updateToolCall(id, partial); },
        clearToolCalls: () => { if (isBound()) store.clearToolCalls(); },
        requestInputFocus: () => { if (isBound()) store.requestInputFocus(); },
      };
    }

    /**
     * Coalesces per-token onTextDelta calls into a single store write per animation
     * frame, so the store's ChatMessage.content stays in sync with the live stream
     * without forcing a React render per token. Reads turn.accumulatedText live at
     * flush time (not a captured snapshot), so even a late-firing flush writes the
     * correct up-to-date text instead of an earlier partial value.
     */
    protected scheduleStreamingContentFlush(turn: TurnState, ts: TurnScopedStore, assistantId: string): void {
      if (turn.streamFlushHandle != null) return;
      turn.streamFlushHandle = requestAnimationFrame(() => {
        turn.streamFlushHandle = null;
        ts.updateMessage(assistantId, { content: turn.accumulatedText, isStreaming: true });
      });
    }

    /**
     * Must be called immediately before any terminal/authoritative content write
     * (done, error, timeout, interrupt) so a pending flush can't fire afterward and
     * stomp isStreaming back to true, or race the authoritative write.
     */
    protected cancelStreamingContentFlush(turn: TurnState): void {
      if (turn.streamFlushHandle != null) {
        cancelAnimationFrame(turn.streamFlushHandle);
        turn.streamFlushHandle = null;
      }
    }

    protected clearTurn(turnId: string): void {
      const turn = this.turns.get(turnId);
      if (!turn) return;
      clearTimeout(turn.timeoutHandle);
      this.cancelStreamingContentFlush(turn);
      this.turns.delete(turnId);
    }
    
    protected resetTurnTimeout(turn: TurnState): void {
      clearTimeout(turn.timeoutHandle);
      turn.timeoutHandle = setTimeout(() => this.handleTurnTimeout(turn.turnId), CHAT_TIMEOUT_MS);
    }

    protected handleTurnTimeout(turnId: string): void {
      const turn = this.turns.get(turnId);
      if (!turn) return;
      turn.abortReason = 'timeout';
      turn.abortController.abort();
      const ts = this.turnScopedStore(turnId);
      this.cancelStreamingContentFlush(turn);
      const timeoutMessage = t('chatTimeout') || 'The request took too long. Please try again.';
      const partialText = turn.accumulatedText.trim();
      ts.updateMessage(turn.assistantId, {
        content: partialText ? `${partialText}\n\n${timeoutMessage}` : timeoutMessage,
        isStreaming: false,
        turnError: { turnId },
      });
      ts.setState({ isLoading: false });
      ts.requestInputFocus();
      (this.shadow.host as HTMLElement).dispatchEvent(
        new CustomEvent('mcp-chat:turn-timeout', {
          bubbles: true,
          composed: true,
          detail: { turnId },
        })
      );
    }
  };
}
